import * as THREE from 'three';
import { Random } from '../core/Random';
import type { CharacterLook } from '../state/types';
import { buildCharacter, CharacterAnimator } from './builders/character';
import { lawnChair, signBoard } from './builders/props';
import { buildTrailer, type TrailerSpec } from './builders/trailer';
import type { CollisionWorld } from './Collision';
import { LOTS, type Vec2 } from './layout';
import { box, cyl, M, mat } from './materials';

/** Where Lot 10's renovation stands (kept in the save as flags; see game/Landlord.ts). */
export type RentalStage =
  'none' | 'leased' | 'cleared' | 'delivering' | 'trailer' | 'ready' | 'rented' | 'sold';
export type RentalFix = 'roof' | 'windows' | 'steps' | 'skirting' | 'paint';
export const RENTAL_FIXES: readonly RentalFix[] = ['steps', 'windows', 'paint', 'skirting', 'roof'];
export const JUNK_PIECES = ['wall', 'roof sheet', 'studs', 'floor frame'] as const;

const LOT = LOTS.vacant;
const T = LOT.trailer;

/** Lot 10 positions: where each burnt piece and each fix-up gets done from. */
export const LOT10 = {
  junk: [
    { x: T.x - 3.5, z: T.z + 2.9 },
    { x: T.x + 2, z: T.z + 2.9 },
    { x: T.x + 5, z: T.z + 2.9 },
    { x: T.x - 0.6, z: T.z + 2.9 },
  ] as readonly Vec2[],
  fixes: {
    steps: { x: T.x - 4, z: T.z + 3 },
    windows: { x: T.x - 1.7, z: T.z + 3 },
    paint: { x: T.x + 0.7, z: T.z + 3 },
    skirting: { x: T.x + 3.1, z: T.z + 3 },
    roof: { x: T.x + 7.1, z: T.z },
  } as Record<RentalFix, Vec2>,
  sign: { x: 8.8, z: LOT.z + 2.6 },
  tenant: { x: T.x - 1.8, z: T.z + 4.4 },
} as const;

export interface Lot10State {
  stage: RentalStage;
  /** How many burnt pieces have been hauled off. */
  junk: number;
  fixed: readonly RentalFix[];
  paint: string;
  /** Rhonda Mae's out in her lawn chair. */
  tenantOut: boolean;
}

const SIGNS: Record<'asis' | 'leased' | 'soon' | 'forRent' | 'rented' | 'sold', [string, string, string]> = {
  asis: ['FOR RENT', 'AS-IS • CALL DARLENE', '#c62828'],
  leased: ['KEEP OUT', 'PRIVATE PROPERTY (THAT MEANS YOU DALE)', '#c62828'],
  soon: ['COMING SOON', 'LUXURY LIVING • ASK ABOUT IT', '#1d4e9e'],
  forRent: ['FOR RENT', '$120/WK • NO PETS (PETS OK)', '#2f7d32'],
  rented: ['NO VACANCY', 'MANAGED BY A LOCAL', '#2f7d32'],
  sold: ['SOLD', "BIG DAVE'S REALTY", '#c62828'],
};

/** Rhonda Mae: the first tenant of the new Lot 10. Pays in quarters. Has opinions. */
export const RHONDA_LOOK: CharacterLook = {
  body: 'woman',
  skin: '#e8b892',
  hair: 'teased',
  hairColor: '#c94f2e',
  beard: 'none',
  top: 'leopard',
  topColor: '#d9a441',
  bottom: 'sweats',
  bottomColor: '#3a3a4a',
  shoes: 'slippers',
  shoeColor: '#ff9ac8',
  hat: 'none',
  hatColor: '#333333',
  belly: 0.4,
  height: 0.95,
  shades: true,
};

/**
 * Lot 10, across Main Street from Jenkins: a burnt-out wreck until somebody leases it,
 * hauls the junk off, drops a repo single-wide on it and fixes that up. Everything here
 * is rebuilt from a small state (see Lot10State), so a loaded game looks right.
 */
export class Lot10Site {
  readonly group = new THREE.Group();
  /** The burnt pieces, in JUNK_PIECES order. */
  readonly pieces: THREE.Group[] = [];
  private readonly grass: THREE.InstancedMesh;
  private sign: THREE.Group | null = null;
  private signKey = '';
  private trailer: THREE.Group | null = null;
  private trailerKey = '';
  private readonly tenant: { root: THREE.Group; anim: CharacterAnimator };
  private ids: number[] = [];
  private shapeKey = '';
  /** Keeps the camera from swinging through the trailer, like the other homes. */
  private readonly occluder = new THREE.Box3(
    new THREE.Vector3(T.x - 6, 0, T.z - 2),
    new THREE.Vector3(T.x + 6, 3.4, T.z + 2),
  );

  constructor(
    parent: THREE.Object3D,
    private readonly collision: CollisionWorld,
    private readonly occluders: THREE.Box3[],
  ) {
    this.group.name = 'lot10';
    parent.add(this.group);
    const rng = new Random(1010);
    const charred = mat('#2b241f', { rough: 1 });
    // The wreck, in four haul-able pieces.
    const wall = new THREE.Group();
    wall.add(box(4, 1.8, 0.1, charred, -3.5, 1.5, -1.95));
    for (let x = -5.8; x <= 5.8; x += 1.45)
      if (rng.chance(0.6)) {
        const h = rng.range(0.6, 2.4) * 0.8;
        wall.add(box(0.15, h, 0.15, charred, x, 0.6 + h / 2, -1.95));
      }
    const roof = new THREE.Group();
    const sheet = box(5, 0.1, 3.4, mat('#4a4744', { rough: 0.8 }), 2, 1.1, 0);
    sheet.rotation.z = 0.35;
    roof.add(sheet);
    const studs = new THREE.Group();
    for (let x = -5.8; x <= 5.8; x += 1.45) {
      const h = rng.range(0.6, 2.4);
      studs.add(box(0.15, h, 0.15, charred, x, 0.6 + h / 2, 1.95));
    }
    const floor = new THREE.Group();
    floor.add(box(12, 0.25, 4, charred, 0, 0.5, 0));
    for (const x of [-1.2, 1.2])
      for (const z of [-1.4, 1.4]) {
        const w = cyl(0.32, 0.32, 0.18, M.rubber, x, 0.32, z, 12);
        w.rotation.x = Math.PI / 2;
        floor.add(w);
      }
    for (const piece of [wall, roof, studs, floor]) {
      piece.position.set(T.x, 0, T.z);
      this.group.add(piece);
      this.pieces.push(piece);
    }
    // Overgrown grass that nobody mows (until there's money in it).
    const blade = new THREE.ConeGeometry(0.16, 0.9, 4);
    blade.translate(0, 0.45, 0);
    const count = 260;
    this.grass = new THREE.InstancedMesh(blade, mat('#6b8f3a', { flat: true }), count);
    const m4 = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    for (let i = 0; i < count; i++) {
      const x = rng.range(6, 27);
      const z = rng.range(LOT.z - 8, LOT.z + 7.5);
      const s = rng.range(0.6, 1.4);
      q.setFromEuler(new THREE.Euler(rng.range(-0.2, 0.2), rng.range(0, 6), rng.range(-0.2, 0.2)));
      m4.compose(new THREE.Vector3(x, 0, z), q, new THREE.Vector3(s, s * rng.range(0.7, 1.3), s));
      this.grass.setMatrixAt(i, m4);
    }
    this.grass.castShadow = false;
    this.grass.receiveShadow = true;
    this.group.add(this.grass);
    // The tenant and her chair (only out once there is one).
    const rig = buildCharacter(RHONDA_LOOK);
    const chair = lawnChair('#d94f9a');
    const root = new THREE.Group();
    root.add(chair, rig.root);
    root.position.set(LOT10.tenant.x, 0, LOT10.tenant.z);
    root.visible = false;
    this.group.add(root);
    this.tenant = { root, anim: new CharacterAnimator(rig) };
    this.group.traverse((o) => (o.userData.noBatch = true));
    this.apply({ stage: 'none', junk: 0, fixed: [], paint: '#c9dcf0', tenantOut: false });
  }

  /** The thing each fix-up spot is about (for the highlight outline). */
  get trailerModel(): THREE.Group | null {
    return this.trailer;
  }

  /** Make Lot 10 look the way the save says it should. */
  apply(st: Lot10State): void {
    const wrecked = st.stage === 'none' || st.stage === 'leased';
    this.pieces.forEach((p, i) => (p.visible = wrecked && i >= st.junk));
    this.grass.visible = wrecked;
    const hasTrailer =
      st.stage === 'trailer' || st.stage === 'ready' || st.stage === 'rented' || st.stage === 'sold';
    const spec = hasTrailer ? this.spec(st) : null;
    const key = spec ? JSON.stringify(spec) : '';
    if (key !== this.trailerKey) {
      this.trailerKey = key;
      this.trailer?.removeFromParent();
      this.trailer = null;
      if (spec) {
        const t = buildTrailer(spec);
        if (!st.fixed.includes('roof')) t.add(roofHoles());
        t.position.set(T.x, 0, T.z);
        t.traverse((o) => (o.userData.noBatch = true));
        this.group.add(t);
        this.trailer = t;
      }
    }
    const sign =
      st.stage === 'none'
        ? 'asis'
        : st.stage === 'leased'
          ? 'leased'
          : st.stage === 'cleared' || st.stage === 'delivering' || st.stage === 'trailer'
            ? 'soon'
            : st.stage === 'ready'
              ? 'forRent'
              : st.stage === 'rented'
                ? 'rented'
                : 'sold';
    if (sign !== this.signKey) {
      this.signKey = sign;
      this.sign?.removeFromParent();
      const [big, small, color] = SIGNS[sign];
      const s = signBoard(
        {
          w: 512,
          h: 256,
          bg: '#ffffff',
          fg: color,
          border: color,
          borderWidth: 12,
          lines: [
            { text: big, size: 90, font: 'Anton' },
            { text: small, size: 34, font: 'Anton', color: '#111' },
          ],
          grime: true,
        },
        1.6,
        0.8,
        0.9,
      );
      s.position.set(LOT10.sign.x, 0, LOT10.sign.z);
      s.rotation.y = -Math.PI / 2 + 0.25;
      s.traverse((o) => (o.userData.noBatch = true));
      this.group.add(s);
      this.sign = s;
    }
    this.tenant.root.visible = st.stage === 'rented' && st.tenantOut;
    this.collide(st, wrecked, hasTrailer);
  }

  private spec(st: Lot10State): TrailerSpec {
    const has = (f: RentalFix): boolean => st.fixed.includes(f);
    const painted = has('paint');
    return {
      color: painted ? st.paint : '#c8bfa4',
      stripe: painted ? '#f4f1e6' : undefined,
      rusty: !painted,
      doorX: -LOT.side * 4,
      hitch: -LOT.side,
      windows: [
        { x: -1.5, side: 1, kind: has('windows') ? 'blinds' : 'boarded' },
        { x: 2.4, side: 1, kind: has('windows') ? 'glass' : 'cardboard' },
        { x: 0.4, side: -1, kind: has('windows') ? 'glass' : 'boarded' },
      ],
      skirting: has('skirting') ? 'lattice' : 'none',
      steps: has('steps') ? 'blocks' : 'none',
      tarp: has('roof'),
      porchLight: painted,
      seed: 10,
    };
  }

  private collide(st: Lot10State, wrecked: boolean, hasTrailer: boolean): void {
    const key = `${wrecked}|${hasTrailer}|${st.fixed.includes('steps')}`;
    if (key === this.shapeKey) return;
    this.shapeKey = key;
    for (const id of this.ids) this.collision.remove(id);
    this.ids = [];
    if (wrecked || hasTrailer) this.ids.push(this.collision.addRect(T.x, T.z, 12, 4));
    if (hasTrailer) this.ids.push(this.collision.addRect(T.x - LOT.side * 7.2, T.z, 2.2, 1.2));
    if (hasTrailer && st.fixed.includes('steps'))
      this.ids.push(this.collision.addRect(LOT.door.x, LOT.door.z + 0.45, 1.0, 0.9));
    const i = this.occluders.indexOf(this.occluder);
    if (hasTrailer && i < 0) this.occluders.push(this.occluder);
    else if (!hasTrailer && i >= 0) this.occluders.splice(i, 1);
  }

  /** Rhonda rocks in her lawn chair with a beer. */
  update(dt: number, time: number): void {
    if (!this.tenant.root.visible) return;
    this.tenant.anim.update({ dt, time, speed: 0, pose: 'sitdrink', seat: 0.42 });
  }
}

/** Holes in the roof (fixed with a blue tarp, the traditional way). */
function roofHoles(): THREE.Group {
  const g = new THREE.Group();
  const hole = mat('#141210', { rough: 1 });
  for (const [x, z, w, d] of [
    [-2.6, 0.4, 1.4, 1.1],
    [1.8, -0.6, 0.9, 0.8],
    [3.9, 0.7, 0.7, 0.6],
  ] as const)
    g.add(box(w, 0.02, d, hole, x, 0.55 + 2.5 + 0.215, z, { cast: false }));
  return g;
}

/** A charred chunk of the old trailer, carried over your head to Junkyard Jerry. */
export function junkChunk(i: number): THREE.Group {
  const g = new THREE.Group();
  const charred = mat('#2b241f', { rough: 1 });
  if (i === 1) g.add(box(1.8, 0.06, 1.2, mat('#4a4744', { rough: 0.8 }), 0, 0.03, 0));
  else if (i === 2)
    for (let k = 0; k < 5; k++)
      g.add(box(1.7, 0.12, 0.12, charred, 0, 0.06 + (k % 2) * 0.12, -0.24 + k * 0.12));
  else if (i === 3) {
    g.add(box(1.6, 0.12, 0.12, M.steel, 0, 0.3, 0));
    for (const x of [-0.75, 0.75]) {
      const w = cyl(0.3, 0.3, 0.16, M.rubber, x, 0.3, 0, 12);
      w.rotation.z = Math.PI / 2;
      g.add(w);
    }
  } else g.add(box(1.6, 1.0, 0.08, charred, 0, 0.5, 0));
  g.traverse((o) => (o.userData.noBatch = true));
  return g;
}
